import React, { useState } from 'react';
import { 
  FiCheckCircle, 
  FiXCircle, 
  FiClock, 
  FiAlertTriangle,
  FiRefreshCw,
  FiChevronDown,
  FiChevronRight,
  FiFolder
} from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

interface DocumentStatusItem {
  file_id: string;
  file_name: string;
  file_path?: string;
  is_embedded: boolean;
  last_embedded?: string;
  embedding_status?: string;
  chunk_count?: number;
  version?: number;
  source_folder_name?: string;
  source_folder_id?: string;
  source_collection?: string;
}

interface DocumentStatusTableProps {
  documents: DocumentStatusItem[];
  onRefresh: () => void;
}

interface FolderGroup {
  folderName: string;
  folderId: string;
  documents: DocumentStatusItem[];
  totalCount: number;
  embeddedCount: number;
  pendingCount: number;
}

type SortField = 'file_name' | 'is_embedded' | 'last_embedded' | 'chunk_count';
type SortDirection = 'asc' | 'desc';

const DocumentStatusTable: React.FC<DocumentStatusTableProps> = ({ 
  documents, 
  onRefresh
}) => {
  const [sortField, setSortField] = useState<SortField>('file_name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // Group documents by folder
  const groupedDocuments = React.useMemo(() => {
    const groups: { [key: string]: FolderGroup } = {};
    
    documents.forEach(doc => {
      const folderName = doc.source_folder_name || 'Cartella Sconosciuta';
      const folderId = doc.source_folder_id || 'unknown';
      
      if (!groups[folderId]) {
        groups[folderId] = {
          folderName,
          folderId,
          documents: [],
          totalCount: 0,
          embeddedCount: 0,
          pendingCount: 0
        };
      }
      
      groups[folderId].documents.push(doc);
      groups[folderId].totalCount++;
      
      if (doc.is_embedded) {
        groups[folderId].embeddedCount++;
      } else {
        groups[folderId].pendingCount++;
      }
    });
    
    // Sort folders by name and sort documents within each folder
    return Object.values(groups)
      .sort((a, b) => a.folderName.localeCompare(b.folderName))
      .map(group => ({
        ...group,
        documents: group.documents.sort((a, b) => {
          switch (sortField) {
            case 'file_name':
              return sortDirection === 'asc' 
                ? a.file_name.localeCompare(b.file_name)
                : b.file_name.localeCompare(a.file_name);
            case 'is_embedded':
              if (sortDirection === 'asc') {
                return a.is_embedded === b.is_embedded ? 0 : a.is_embedded ? -1 : 1;
              } else {
                return a.is_embedded === b.is_embedded ? 0 : a.is_embedded ? 1 : -1;
              }
            case 'last_embedded':
              if (!a.last_embedded && !b.last_embedded) return 0;
              if (!a.last_embedded) return sortDirection === 'asc' ? 1 : -1;
              if (!b.last_embedded) return sortDirection === 'asc' ? -1 : 1;
              return sortDirection === 'asc'
                ? new Date(a.last_embedded).getTime() - new Date(b.last_embedded).getTime()
                : new Date(b.last_embedded).getTime() - new Date(a.last_embedded).getTime();
            case 'chunk_count':
              const countA = a.chunk_count || 0;
              const countB = b.chunk_count || 0;
              return sortDirection === 'asc' ? countA - countB : countB - countA;
            default:
              return 0;
          }
        })
      }));
  }, [documents, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const expandAllFolders = () => {
    setExpandedFolders(new Set(groupedDocuments.map(group => group.folderId)));
  };

  const collapseAllFolders = () => {
    setExpandedFolders(new Set());
  };

  // Get status indicator
  const getStatusIndicator = (document: DocumentStatusItem) => {
    if (!document.is_embedded) {
      return {
        icon: <FiClock className="h-4 w-4 text-yellow-500" />,
        text: 'Non indicizzato',
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/20'
      };
    }

    switch (document.embedding_status?.toUpperCase()) {
      case 'EMBEDDED':
        return {
          icon: <FiCheckCircle className="h-4 w-4 text-green-500" />,
          text: 'Indicizzato',
          color: 'text-green-500',
          bgColor: 'bg-green-100 dark:bg-green-900/20'
        };
      case 'FAILED':
        return {
          icon: <FiXCircle className="h-4 w-4 text-red-500" />,
          text: 'Fallito',
          color: 'text-red-500',
          bgColor: 'bg-red-100 dark:bg-red-900/20'
        };
      default:
        return {
          icon: <FiAlertTriangle className="h-4 w-4 text-gray-500" />,
          text: 'Sconosciuto',
          color: 'text-gray-500',
          bgColor: 'bg-gray-100 dark:bg-gray-800'
        };
    }
  };

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const formatLastEmbedded = (dateString?: string) => {
    if (!dateString) return 'Mai';
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: it });
    } catch (e) {
      return 'Data non valida';
    }
  };

  if (documents.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
        <p className="text-gray-600 dark:text-gray-400">Nessun documento trovato corrispondente ai tuoi criteri</p>
        <button 
          onClick={onRefresh}
          className="mt-4 inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-200 rounded-md hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
        >
          <FiRefreshCw className="mr-2" />
          Aggiorna
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
      {/* Controls */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              Documenti per Cartella ({groupedDocuments.length} cartelle)
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={expandAllFolders}
                className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                Espandi Tutto
              </button>
              <button
                onClick={collapseAllFolders}
                className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Comprimi Tutto
              </button>
            </div>
          </div>
          
          {/* Sort controls */}
          <div className="flex items-center space-x-2 text-xs">
            <span className="text-gray-500 dark:text-gray-400">Ordina per:</span>
            <select
              value={sortField}
              onChange={(e) => handleSort(e.target.value as SortField)}
              className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs"
            >
              <option value="file_name">Nome</option>
              <option value="is_embedded">Stato</option>
              <option value="last_embedded">Data</option>
              <option value="chunk_count">Frammenti</option>
            </select>
          </div>
        </div>
      </div>

      {/* Folder groups */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {groupedDocuments.map((group) => {
          const isExpanded = expandedFolders.has(group.folderId);
          const progressPercentage = group.totalCount > 0 ? (group.embeddedCount / group.totalCount) * 100 : 0;
          
          return (
            <div key={group.folderId} className="bg-white dark:bg-gray-800">
              {/* Folder header */}
              <div 
                className="px-6 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => toggleFolder(group.folderId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center">
                      {isExpanded ? (
                        <FiChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <FiChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                      <FiFolder className="h-5 w-5 text-blue-500 ml-2 mr-3" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {group.folderName}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {group.totalCount} documenti • {group.embeddedCount} indicizzati • {group.pendingCount} non indicizzati
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {/* Mini progress bar */}
                    <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(100, progressPercentage)}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      {Math.round(progressPercentage)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Documents table */}
              {isExpanded && (
                <div className="bg-gray-50 dark:bg-gray-900">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-100 dark:bg-gray-800">
                        <tr>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Nome Documento {getSortIndicator('file_name')}
                          </th>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Stato {getSortIndicator('is_embedded')}
                          </th>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Ultima Indicizzazione {getSortIndicator('last_embedded')}
                          </th>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Frammenti {getSortIndicator('chunk_count')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {group.documents.map((doc) => {
                          const status = getStatusIndicator(doc);
                          
                          return (
                            <tr key={doc.file_id} className="hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                              <td className="px-6 py-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {doc.file_name}
                                </div>
                                {doc.file_path && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {doc.file_path.length > 50 ? '...' + doc.file_path.slice(-50) : doc.file_path}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-3">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                                  {status.icon}
                                  <span className="ml-1">{status.text}</span>
                                </span>
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {formatLastEmbedded(doc.last_embedded)}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {doc.chunk_count || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentStatusTable; 